import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { fadeUp, viewport } from "@/lib/motion";
import { TREATMENT_MEDIA } from "@/data";
import { useLang } from "@/context/LanguageContext";

export default function TreatmentPage() {
  const { key } = useParams();
  const { t } = useLang();
  const tp = t.treatmentPage;
  const data = t.treatments[key];
  const media = TREATMENT_MEDIA[key];

  useEffect(() => { window.scrollTo(0, 0); }, [key]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7F6F2]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-32">
          <p className="font-serif text-4xl text-[#0D2A27]">{tp.notFound}</p>
          <Link to="/#services" className="mt-6 inline-block text-[#C4A484] font-semibold">← {tp.back}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#F7F6F2]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <Link to="/#services" data-testid="treatment-back" className="overline text-[#555] hover:text-[#0D2A27] transition-colors">← {tp.back}</Link>
              <p className="overline text-[#C4A484] mt-8 mb-4">{data.tag}</p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight text-[#0D2A27]">{data.name}</h1>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }} className="lg:col-span-6">
            <div className="relative h-[300px] md:h-[380px] overflow-hidden border border-[#E2E0D8] rounded-tr-[120px]">
              <img src={media.hero} alt={data.name} className="h-full w-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview + Included */}
      <section className="py-16 md:py-24 border-t border-[#E2E0D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="lg:col-span-7">
            <p className="overline text-[#C4A484] mb-5">{tp.overviewTitle}</p>
            <p className="font-serif text-2xl md:text-3xl leading-[1.3] text-[#0D2A27]">{data.overview}</p>
          </motion.div>
          <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={viewport} className="lg:col-span-5">
            <p className="overline text-[#C4A484] mb-6">{tp.includedTitle}</p>
            <ul className="divide-y divide-[#E2E0D8] border-t border-[#E2E0D8]">
              {data.included.map((item, i) => (
                <li key={i} className="flex items-start gap-4 py-4">
                  <span className="text-[#C4A484] mt-1">✦</span>
                  <span className="text-[#111]">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-[#EDECE6] border-y border-[#E2E0D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-[#C4A484] mb-10">{tp.timelineTitle}</p>
          <div className="grid md:grid-cols-4 gap-6">
            {data.timeline.map((step, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={viewport} className="bg-[#F7F6F2] border border-[#E2E0D8] p-6">
                <p className="font-serif text-xl text-[#C4A484]">{step.phase}</p>
                <p className="mt-3 text-sm text-[#555] leading-relaxed">{step.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline text-[#C4A484] mb-10">{tp.galleryTitle}</p>
          <div className="grid md:grid-cols-3 gap-4">
            {media.gallery.map((src, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={viewport} className={`relative overflow-hidden border border-[#E2E0D8] ${i === 0 ? "md:row-span-2 h-[300px] md:h-[500px]" : "h-[300px] md:h-[242px]"}`}>
                <img src={src} alt={`${data.name} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out hover:scale-105" />
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#555] italic">{tp.disclaimer}</p>
        </div>
      </section>

      {/* Treatment FAQ */}
      <section className="py-16 md:py-24 bg-[#EDECE6] border-y border-[#E2E0D8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="overline text-[#C4A484] mb-10">{tp.faqTitle}</p>
          <div className="divide-y divide-[#E2E0D8] border-t border-[#E2E0D8]">
            {data.faqs.map((item, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={viewport} className="py-7">
                <h3 className="font-serif text-xl md:text-2xl text-[#0D2A27]">{item.q}</h3>
                <p className="mt-3 text-[#555] leading-relaxed">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Contact defaultService={data.name} />
      <Footer />
    </div>
  );
}
